import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './mapMarkers.scss';
import { useLanguage } from '../../LanguageContext';

// Adatta la vista ai risultati della ricerca (non alla finestra temporale:
// durante la navigazione nel tempo la cornice resta stabile)
const FitToMarkers = ({ markers, defaultCenter, defaultZoom }) => {
    const map = useMap();
    useEffect(() => {
        if (markers.length === 0) {
            map.setView(defaultCenter, defaultZoom);
        } else if (markers.length === 1) {
            map.setView(
                [markers[0].location.latitude, markers[0].location.longitude], 8);
        } else {
            const bounds = L.latLngBounds(
                markers.map(m => [m.location.latitude, m.location.longitude]));
            map.fitBounds(bounds, { padding: [60, 60], maxZoom: 9 });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markers, map]);
    return null;
};

// Cerchio proporzionale al numero di opere attive nel periodo
const makeCircleIcon = (count, { dim, uncertain, gold }) => {
    const size = dim ? 14 : Math.min(46, 24 + count * 3);
    const cls = [
        'bs-marker',
        dim ? 'bs-dim' : '',
        uncertain ? 'bs-uncertain' : '',
        gold ? 'bs-gold' : '',
    ].filter(Boolean).join(' ');
    return L.divIcon({
        className: '',
        html: `<div class="${cls}" style="width:${size}px;height:${size}px;">`
              + `${dim ? '' : count}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2],
    });
};

const MapMultiPoint = ({ points, setActiveArtwork, setActiveArtworksList,
                         selectedArtworkId, hoveredArtworkId,
                         activeIds, onShowAllTime }) => {
    const defaultCenter = [41.50, 12.35];
    const defaultZoom = 5;
    const { selectedLanguage } = useLanguage();
    const it = selectedLanguage === 'it';
    const getLangValue = (data) =>
        data ? data[it ? 'it_IT' : 'en_GB'] : '';

    // Raggruppa i punti per coordinate uniche per evitare marker duplicati
    const uniqueMarkers = useMemo(() => {
        const grouped = {};
        points.forEach(p => {
            if (p.location && p.location.latitude && p.location.longitude) {
                const key = `${p.location.latitude}_${p.location.longitude}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        location: p.location,
                        artworks: [],
                    };
                }
                grouped[key].artworks.push(p);
            }
        });
        return Object.values(grouped);
    }, [points]);

    // opere del marker attive nella finestra temporale corrente
    const activeOf = (marker) => (activeIds
        ? marker.artworks.filter(a => activeIds.has(a.id))
        : marker.artworks);

    const handleMarkerClick = (marker) => {
        const active = activeOf(marker);
        if (active.length > 0) {
            setActiveArtwork(active[0]);
            setActiveArtworksList(active);
        }
    };

    const selectArtwork = (marker, artwork) => {
        setActiveArtwork(artwork);
        setActiveArtworksList(activeOf(marker));
    };

    const isHighlighted = (marker) =>
        (selectedArtworkId
            && marker.artworks.some(a => a.id === selectedArtworkId))
        || (hoveredArtworkId
            && marker.artworks.some(a => a.id === hoveredArtworkId));

    return (
        <MapContainer center={defaultCenter} zoom={defaultZoom} style={{ height: 'inherit', width: '100%' }}>
        <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
        />
        <FitToMarkers markers={uniqueMarkers}
                      defaultCenter={defaultCenter} defaultZoom={defaultZoom} />
        {uniqueMarkers.map((marker) => {
            const active = activeOf(marker);
            const dim = active.length === 0;
            const outOfPeriod = marker.artworks.length - active.length;
            return (
            <Marker
                key={`${marker.location.latitude}_${marker.location.longitude}`}
                position={[marker.location.latitude, marker.location.longitude]}
                icon={makeCircleIcon(active.length, {
                    dim,
                    uncertain: marker.location.is_uncertain,
                    gold: isHighlighted(marker),
                })}
                zIndexOffset={isHighlighted(marker) ? 1000 : (dim ? -500 : 0)}
                eventHandlers={{
                    click: () => handleMarkerClick(marker),
                }}
            >
            <Popup maxWidth={280}>
                <div style={{ fontSize: '13px' }}>
                    <strong>
                        {[marker.location.city_town, marker.location.name_off]
                            .filter(Boolean).join(', ')}
                        {marker.location.is_uncertain &&
                            (it ? ' (luogo incerto)' : ' (uncertain place)')}
                    </strong>
                    <div style={{ color: '#666', margin: '2px 0 6px' }}>
                        {active.length}{' '}
                        {it
                            ? (active.length === 1 ? 'opera nel periodo' : 'opere nel periodo')
                            : (active.length === 1 ? 'work in this period' : 'works in this period')}
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '16px', maxHeight: '140px', overflowY: 'auto' }}>
                        {active.map((a) => (
                            <li key={a.id} style={{ marginBottom: '3px' }}>
                                <button
                                    type="button"
                                    onClick={() => selectArtwork(marker, a)}
                                    style={{
                                        background: 'none', border: 'none',
                                        padding: 0, color: '#795f1c',
                                        textDecoration: 'underline',
                                        cursor: 'pointer', textAlign: 'left',
                                        fontWeight: a.id === selectedArtworkId ? 700 : 400,
                                    }}
                                >
                                    {getLangValue(a.name)}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {outOfPeriod > 0 && (
                        <button
                            type="button"
                            onClick={() => onShowAllTime && onShowAllTime()}
                            style={{
                                background: 'none', border: 'none', padding: 0,
                                marginTop: '6px', color: '#8e2a3d',
                                textDecoration: 'underline', cursor: 'pointer',
                                fontSize: '12px',
                            }}
                        >
                            {it
                                ? `+ ${outOfPeriod} in altri periodi — mostra tutti i secoli`
                                : `+ ${outOfPeriod} in other periods — show all centuries`}
                        </button>
                    )}
                </div>
            </Popup>
            </Marker>
            );
        })}
        </MapContainer>
    );
};

export default MapMultiPoint;
